import { css } from '@emotion/core'
import React from 'react'
import { SocialChat } from '@fs/zion-icon'
import { IconButton, ListItem, MenuOverlay, useMenuOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useRouteMatch } from '@fs/zion-router'
import { boolAttr } from '@fs/zion-frontend-friends'
import { buildChatRoute, CHAT_ROUTE_KEY } from '@fs/zion-chat'
import AUAChatButton from '../AUAChatButton'
import PillButton from '../PillButton'
import { useAnonCanAddPersonalData, useIsMdWidth } from '../../../hooks'
import { floatingChatConfig } from '../../../config'
import { useChatContext } from '../../../providers'
import ChatButton from '../../../components/header/ChatButton'

const floatingCss = css`
  position: fixed;
  inset-inline-end: 20px;
  bottom: 40px;
  z-index: 1;
  white-space: nowrap;
`

const ClosingListItem = React.forwardRef(({ onClick, onClose, ...props }, ref) => {
  const handleClick = (e) => {
    // Call the button's onClick handler first (opens chat)
    if (onClick) onClick(e)
    // Then close the overlay
    onClose?.()
  }

  return <ListItem ref={ref} {...props} onClick={handleClick} />
})

export const FloatingChatButtonOverlay = ({ ...overlay }) => {
  const [t] = useTranslation()

  return (
    <MenuOverlay {...overlay}>
      <AUAChatButton
        data-testid="floating-chatButton-askQuestion"
        linkName="FloatingChatButton: Ask a Question"
        asComponent={ClosingListItem}
        onClose={overlay.close}
        primaryText={t('chat.floating-button.ask-question', 'Ask a Question')}
        dense
      />
      <ChatButton
        data-testid="floating-chatButton-openChats"
        linkName="FloatingChatButton: Open all Chats"
        asComponent={ClosingListItem}
        onClose={overlay.close}
        chatRoute={buildChatRoute(CHAT_ROUTE_KEY.CHATS)}
        primaryText={t('chat.floating-button.open-chats', 'Open all Chats')}
        dense
      />
    </MenuOverlay>
  )
}

const FloatingChatButton = () => {
  const [t] = useTranslation()
  const chatFlag = useFeatureFlag('rootstech_chat')
  const auaButtonFlag = useFeatureFlag('messaging_chat_aua_button')
  const isMdWidth = useIsMdWidth()
  const blacklistMatch = useRouteMatch(floatingChatConfig.ROUTE_BLACKLIST)
  const { isChatApproved } = useChatContext()
  const overlay = useMenuOverlay()

  const canUsePersonalData = useAnonCanAddPersonalData()

  const Tag = isMdWidth ? IconButton : PillButton

  if (blacklistMatch || !isChatApproved || !canUsePersonalData) {
    return null
  }

  return (
    chatFlag.isOn && (
      <div css={floatingCss} is-md-width={boolAttr(isMdWidth)}>
        {auaButtonFlag.isOn ? (
          <Tag
            color="blue"
            data-testid="floating-chat-button"
            linkName="Chat: Floating Button"
            Icon={SocialChat}
            emphasis="high"
            size={isMdWidth ? 'md' : 'lg'}
            onClick={overlay.handleClick}
            {...(isMdWidth && { active: overlay.isOpen })}
            aria-label={isMdWidth ? t('chat.button', 'Chat') : undefined}
          >
            {t('chat.button', 'Chat')}
          </Tag>
        ) : (
          <ChatButton
            asComponent={isMdWidth ? IconButton : PillButton}
            chatRoute={buildChatRoute(CHAT_ROUTE_KEY.CHATS)}
            color="blue"
            Icon={SocialChat}
            emphasis="high"
            size={isMdWidth ? 'md' : 'lg'}
          >
            {t('chat.button', 'Chat')}
          </ChatButton>
        )}
        {(overlay.isOpen || overlay.transitioning) && <FloatingChatButtonOverlay {...overlay} />}
      </div>
    )
  )
}

export default FloatingChatButton
